import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type {
    IServiceGroupWithServices,
    IServiceOrganizationSettings,
} from '../../../../../common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { ValidationErrors } from '../../../../utils/validation';
import type {
    ChooseCarePathStep,
    ICareProviderAddCarePathState,
} from '../../../interfaces/careProviderAddCarePathState';
import { translateKey } from '../../../../translate';
import { createEnumeration } from '../../../../utils/enumeration';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { PersonalSettingsAddCarePathServiceOrganizationSettings } from './PersonalSettingsAddCarePathServiceOrganizationSettings';

interface IOrganizationSettingsProps {
    errors?: ValidationErrors;
    state: ICareProviderAddCarePathState;
    updateState: (state: Partial<ICareProviderAddCarePathState>, carePathStep: ChooseCarePathStep) => void;
}

export function OrganizationSettings(props: IOrganizationSettingsProps) {
    const abortableFetch = useAbortableFetch();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [carePathHasServiceOrganizationSettings, setCarePathHasServiceOrganizationSettings] =
        useState<boolean>(false);

    useEffect(() => {
        load();
    }, [props.state.episodeCategory]);

    return (
        <>
            {!isLoading && <Alert appearance="info">{getAlertMessage()}</Alert>}
            <PersonalSettingsAddCarePathServiceOrganizationSettings
                errors={props.errors}
                state={props.state}
                updateState={props.updateState}
            />
        </>
    );

    function getAlertMessage() {
        if (!carePathHasServiceOrganizationSettings) {
            return translateKey(
                'carePath.missingServiceOrganizationSettings.organization',
                translateKey(`episodeCategoryEnum.${props.state.episodeCategory?.name}`)
            );
        } else {
            const serviceGroupsWithMissingServiceOrganizationSettings = (props.state.serviceGroups ?? []).filter(
                (serviceGroup) => serviceGroup.serviceOrganizationSettings?.length
            );

            return translateKey(
                `carePath.missingServiceOrganizationSettings.${
                    serviceGroupsWithMissingServiceOrganizationSettings.length > 1 ? 'serviceGroups' : 'serviceGroup'
                }`,
                getEnumeratedServiceGroupsNames(serviceGroupsWithMissingServiceOrganizationSettings)
            );
        }
    }

    function getEnumeratedServiceGroupsNames(serviceGroups: IServiceGroupWithServices[]) {
        return createEnumeration(serviceGroups.map((serviceGroup) => serviceGroup.name));
    }

    async function load() {
        const serviceOrganizationSettingsForEpisodeCategoryAndOrganization = (
            await abortableFetch.fetchJson(
                `/api/serviceOrganizationSettings?organizationId=${props.state.employment?.organizationId}&episodeCategory=${props.state.episodeCategory?.name}&isActive=true`,
                'GET'
            )
        ).json.items as IServiceOrganizationSettings[];
        setCarePathHasServiceOrganizationSettings(
            Boolean(serviceOrganizationSettingsForEpisodeCategoryAndOrganization.length)
        );
        setIsLoading(false);
    }
}
